/**
 * INV-04 · Uma única definição de "quais são os segmentos de negócio".
 *
 * A lista de segmentos atravessa quatro camadas, e as quatro precisam falar a
 * MESMA string:
 *
 *   1. o enum do Prisma ........... `BusinessSegment` (backend/prisma/schema.prisma)
 *   2. as constantes do backend ... `backend/src/constants/segments.ts`
 *   3. o corpo do onboarding ...... `POST /tenants/setup { segment }`
 *   4. o menu lateral ............. `buildMenu.js` filtra itens por segmento
 *
 * Até 21/08/2026 o frontend tinha DUAS cópias no mesmo arquivo:
 * `SEGMENT_OPTIONS` (com ícone/nome/descrição) e `VALID_SEGMENTS` (só os
 * valores), ambas escritas à mão dentro do `TenantSetupModal`. Duas listas do
 * mesmo conjunto divergem em silêncio: um segmento novo em `SEGMENT_OPTIONS`
 * aparece como card clicável, mas some da pré-seleção porque `VALID_SEGMENTS`
 * não o conhece — e ninguém vê erro nenhum. Por isso `VALID_SEGMENTS` agora é
 * DERIVADO de `SEGMENT_OPTIONS`, não redigitado.
 *
 * ⚠️ O espelho no backend é `backend/src/constants/segments.ts`, que por sua vez
 * espelha o enum `BusinessSegment` do Prisma. Mexeu num, mexa nos outros — e
 * segmento novo exige migration antes de chegar aqui.
 */

/**
 * Os segmentos vendidos, na ordem em que aparecem no onboarding.
 *
 * `value` é o contrato com o backend (o literal do enum do Prisma). Os demais
 * campos são apresentação: ícone, nome e descrição do card, e os `badges` que
 * dizem o que aquele segmento ganha de recurso.
 */
export const SEGMENT_OPTIONS = Object.freeze([
  {
    value: "barbearias",
    icon: "✂️",
    name: "Barbearia",
    desc: "Salões e barbearias",
    badges: ["Fidelidade", "Agenda IA", "WhatsApp"],
  },
  {
    value: "consultorios",
    icon: "🏥",
    name: "Consultório / Clínica",
    desc: "Consultórios e clínicas médicas",
    badges: ["Triagem", "Plano de saúde", "Agenda IA"],
  },
  {
    value: "mecanicas",
    icon: "🔧",
    name: "Mecânica",
    desc: "Oficinas mecânicas e auto centers",
    badges: ["Dados do veículo", "Diagnóstico", "Agenda IA"],
  },
  {
    value: "lava_jatos",
    icon: "🚗",
    name: "Lava-Jato",
    desc: "Lava-jatos e estéticas automotivas",
    badges: ["Upsell automático", "Agenda IA", "WhatsApp"],
  },
  {
    value: "academias",
    icon: "🏋️",
    name: "Academia / Escola Esportiva",
    desc: "Futevôlei, beach tennis, musculação e mais",
    badges: ["Integração NextFit", "Agenda IA", "WhatsApp"],
  },
]);

/** Só os valores aceitos pelo backend. DERIVADO — nunca redigitado. */
export const VALID_SEGMENTS = Object.freeze(
  SEGMENT_OPTIONS.map((opt) => opt.value),
);

/**
 * `true` só para um dos valores do enum.
 *
 * Usa `includes` sobre a lista congelada — e não um objeto indexado — para que
 * `"__proto__"`, `"constructor"` e afins não encontrem propriedade herdada do
 * prototype e passem como válidos. Mesmo raciocínio de `isTrialProduct`.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidSegment(value) {
  return VALID_SEGMENTS.includes(value);
}
